import { useEffect, useState } from "react";
import { api, errMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, CheckCircle2, ExternalLink, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { PAYMENT_TYPE_LABEL, PAYMENT_TYPE_DESCRIPTION } from "@/lib/labels";

const DEFAULT_FEE = {
  acis_application_fee: 699,
  acis_renewal_fee: 750,
  ibthm_application_fee: 295,
  ibthm_license_fee: 150,
  ibthm_renewal_fee: 150,
  ibthm_reinstatement_fee: 250,
};

/**
 * Visible "Pay before submit" step. The applicant sees the fee, clicks Pay,
 * is taken through Square checkout (or auto-completes in sandbox), and the
 * widget reports `paid=true` to the parent via `onPaid`.
 *
 * Props:
 *   - paymentType: one of the payment_type keys
 *   - entityType: "application" | "school_application" | "license" | "renewal" | "reinstatement"
 *   - amount?: override fee
 *   - onPaid: callback fired once payment becomes completed/manually_verified
 */
export default function PaymentStep({ paymentType, entityType = "application", amount, onPaid }) {
  const [payment, setPayment] = useState(null);
  const [creating, setCreating] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const fee = amount ?? DEFAULT_FEE[paymentType] ?? 0;

  // Look up an existing unconsumed payment of the same type for this user
  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/payments", { params: { payment_type: paymentType, limit: 20 } });
      const candidate = (r.data || []).find(
        (p) => p.payment_type === paymentType && !p.consumed_by_entity,
      );
      setPayment(candidate || null);
      if (candidate && ["completed", "manually_verified"].includes(candidate.status)) {
        onPaid?.(candidate);
      }
    } catch (e) {
      // not fatal — user simply hasn't paid yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentType]);

  const startPayment = async () => {
    setCreating(true);
    try {
      // Prevent duplicate creation: if there's already a pending payment for this
      // user + payment_type that isn't consumed/failed/expired, re-use it.
      const existing = await api.get("/payments", { params: { payment_type: paymentType, limit: 20 } })
        .then((r) => (r.data || []).find(
          (p) => p.payment_type === paymentType
                 && !p.consumed_by_entity
                 && !["failed", "expired"].includes(p.status),
        ))
        .catch(() => null);
      if (existing && ["completed", "manually_verified"].includes(existing.status)) {
        // Already paid — just refresh state and notify parent.
        setPayment(existing);
        onPaid?.(existing);
        toast.success("Existing payment detected — you're already paid.");
        return;
      }
      if (existing && existing.status === "pending") {
        // Re-use the open pending payment instead of creating another row.
        setPayment(existing);
        if (existing.square_checkout_url) {
          toast.info("Resuming your prior checkout…");
          window.location.href = existing.square_checkout_url;
        } else {
          toast.info("You have a pending payment — complete it below to continue.");
        }
        return;
      }
      const { data } = await api.post("/payments/create-link", {
        payment_type: paymentType,
        entity_type: entityType,
        entity_id: "pending",
        amount: fee,
        currency: "USD",
      });
      setPayment(data);
      if (data.square_checkout_url) {
        toast.success("Redirecting to Square checkout…");
        window.location.href = data.square_checkout_url;
      } else {
        toast.info("Square is in sandbox. Click 'Complete sandbox payment' to proceed.");
      }
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setCreating(false);
    }
  };

  const completeSandbox = async () => {
    if (!payment) return;
    setCompleting(true);
    try {
      const { data } = await api.post(`/payments/${payment.id}/dev-complete`);
      setPayment(data);
      toast.success("Payment recorded. You can submit your application.");
      onPaid?.(data);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setCompleting(false);
    }
  };

  const status = payment?.status;
  const isPaid = ["completed", "manually_verified"].includes(status);
  const isPending = status === "pending";

  return (
    <Card
      className={`p-5 border ${
        isPaid ? "border-emerald-200 bg-emerald-50/50" : "border-amber-200 bg-amber-50/40"
      }`}
      data-testid={`payment-step-${paymentType}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`h-9 w-9 rounded-md flex items-center justify-center flex-shrink-0 ${
            isPaid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
          }`}
        >
          {isPaid ? <CheckCircle2 className="h-5 w-5" /> : <CreditCard className="h-5 w-5" />}
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="font-heading text-sm font-semibold text-primary">
              Step 1 — {PAYMENT_TYPE_LABEL[paymentType] || "Application fee"}
            </h3>
            {isPaid && (
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px]">
                Paid
              </Badge>
            )}
            {isPending && (
              <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]">
                Pending
              </Badge>
            )}
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            {PAYMENT_TYPE_DESCRIPTION[paymentType] || "Payment required before submission."}
          </p>
          <div className="flex flex-wrap items-baseline gap-2 mt-3">
            <span className="text-2xl font-heading font-bold text-primary">${fee}</span>
            <span className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">USD</span>
            {payment?.id && (
              <span className="ml-auto text-[11px] text-slate-500 font-mono-data">
                #{payment.id.slice(0, 8)}
              </span>
            )}
          </div>

          {!isPaid && (
            <div className="mt-3 flex flex-wrap gap-2">
              {!payment ? (
                <Button
                  data-testid={`payment-pay-${paymentType}`}
                  onClick={startPayment}
                  disabled={creating || loading}
                  className="bg-primary text-white hover:bg-[#113A64]"
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" /> Pay ${fee} now
                    </>
                  )}
                </Button>
              ) : payment.square_checkout_url ? (
                <Button
                  data-testid={`payment-checkout-${paymentType}`}
                  onClick={() => (window.location.href = payment.square_checkout_url)}
                  className="bg-[#006AFF] text-white hover:bg-[#0056cc]"
                >
                  <ExternalLink className="h-4 w-4 mr-2" /> Open Square checkout
                </Button>
              ) : (
                <Button
                  data-testid={`payment-dev-complete-${paymentType}`}
                  onClick={completeSandbox}
                  disabled={completing}
                  className="bg-amber-600 text-white hover:bg-amber-700"
                >
                  {completing ? "Completing…" : "Complete sandbox payment"}
                </Button>
              )}
              {payment && !payment.square_checkout_url && (
                <Button
                  variant="outline"
                  onClick={refresh}
                  className="text-xs"
                  data-testid={`payment-refresh-${paymentType}`}
                >
                  Refresh status
                </Button>
              )}
            </div>
          )}

          {payment && !payment.square_checkout_url && payment.fallback_message && !isPaid && (
            <div className="mt-3 rounded-md bg-amber-100/70 border border-amber-200 p-2.5 text-[11px] text-amber-900 flex items-start gap-2">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>{payment.fallback_message}</span>
            </div>
          )}

          {isPaid && (
            <div className="mt-3 text-xs text-emerald-800 flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span>
                Payment verified. You can now complete and submit your application below.
                {payment?.transaction_id && (
                  <span className="font-mono-data text-slate-600 ml-2">{payment.transaction_id}</span>
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
